/**
 * Whether the device editor may hand the hub a `control`/`status` reference
 * payload for one device value (card t_4b64834a).
 *
 * The defect this answers: on Save the editor built a `control`/`status` payload
 * for EVERY value of a `knx` device — `{ propertyType: 'knx', groupAddress: ''
 * }` when the editor's KNX fields were never filled in — and the hub's
 * `PATCH /devices/:id/values/:valueId/references` deletes the value's
 * `control`/`status` rows FIRST and only re-creates them for a payload that
 * carries an address. A demo device's values are bound to `demo` datapoints
 * (`property_type 'demo'`, `protocol_key 'demo://…'`), which that route cannot
 * re-create: one save of an untouched editor silently unbound the whole device
 * and every drive answered 400 `Value is not controllable`.
 *
 * The rule here is the editor only speaks for a binding it can both read and
 * write:
 *
 *   1. a payload that CARRIES a target (a KNX group address, an HA entity id, …)
 *      is always sent — that is the operator binding the value, and it replaces
 *      whatever bound it before;
 *   2. a payload with NO target is sent only when the value is bound to that
 *      very protocol right now — then an empty payload is the operator clearing
 *      that binding, which is exactly what the hub is being asked to do;
 *   3. otherwise (empty fields, and the value is bound to something else or to
 *      nothing) the payload is dropped: there is nothing to say, and sending it
 *      would delete a binding of another protocol.
 */

/** The one field each protocol's payload must carry for the hub to re-create it. */
const TARGET_FIELDS: Record<string, string> = {
  knx: 'groupAddress',
  matter: 'propertyId',
  modbus: 'propertyId',
  mqtt: 'propertyId',
  bacnet: 'propertyId',
  esphome: 'esphomeEntityId',
  zwave: 'zwaveValueId',
  zigbee: 'zigbeeAttributeId',
  homeassistant: 'haEntityId',
  wled: 'wledSegmentId',
};

/** A payload field's text, trimmed — anything that is not a string has no target. */
function textOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Whether a reference payload carries a target the hub can bind a value to.
 *
 * Mirrors the hub's own create conditions for every protocol the editor builds
 * (see `TARGET_FIELDS`). A protocol this table does not know is judged by its
 * own fields: any non-empty string besides the discriminators counts as a
 * target, so a new protocol is sent rather than silently dropped.
 */
export function referenceCarriesTarget(payload: object | null | undefined): boolean {
  if (!payload) return false;
  const bag = payload as Record<string, unknown>;
  const propertyType = textOf(bag.propertyType);
  if (!propertyType) return false;

  const field = TARGET_FIELDS[propertyType];
  if (field) return textOf(bag[field]) !== '';

  return Object.entries(bag).some(
    ([key, value]) =>
      key !== 'propertyType' && key !== 'transformerConfig' && key !== 'integrationId' && textOf(value) !== '',
  );
}

/**
 * Whether the editor should send this payload for a value, given the reference
 * the value carries right now (`controlReference` / `statusReference` off the
 * hub's device row). See the file header for the rule.
 */
export function shouldSendReferencePayload(
  payload: object | null | undefined,
  current: { propertyType?: string | null } | null | undefined,
): boolean {
  if (!payload) return false;
  if (referenceCarriesTarget(payload)) return true;

  const propertyType = textOf((payload as Record<string, unknown>).propertyType);
  return propertyType !== '' && current?.propertyType === propertyType;
}
